import ExcelJS from 'exceljs';

export const CANONICAL_PURCHASE_FIELDS = [
  'Supplier Name',
  'Supplier Code',
  'Supplier Invoice Number',
  'Purchase Date',
  'Shipping Cost (PKR)',
  'Other Purchase Cost (PKR)',
  'Discount (PKR)',
  'Amount Paid (PKR)',
  'Payment Method',
  'Notes',
];

export const CANONICAL_LAPTOP_COLUMNS = [
  'Brand',
  'Model',
  'Serial Number',
  'Service Tag',
  'Barcode',
  'CPU',
  'RAM',
  'Storage',
  'GPU',
  'Screen Size',
  'Resolution',
  'Touchscreen',
  'Colour',
  'Keyboard Layout',
  'Operating System',
  'Condition',
  'Grade',
  'Battery Health %',
  'Charger Included',
  'Charger Notes',
  'Accessories Included',
  'Unit Cost (PKR)',
  'Refurbishment Cost (PKR)',
  'Laptop Other Cost (PKR)',
  'Asking Price (PKR)',
  'Minimum Price (PKR)',
  'Warranty Months',
  'Description',
  'Notes',
  'Public Listing',
];

export const CANONICAL_ACCESSORY_COLUMNS = [
  'SKU',
  'Name',
  'Category',
  'Brand',
  'Barcode',
  'Quantity',
  'Unit Cost (PKR)',
  'Selling Price (PKR)',
  'Reorder Level',
  'Description',
  'Public Listing',
];

export async function generatePurchaseImportTemplate(): Promise<Blob> {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Al Kuwait Tech';
  workbook.created = new Date();

  // 1. Purchase Sheet (Key-Value Metadata)
  const wsPurchase = workbook.addWorksheet('Purchase', { views: [{ showGridLines: true }] });
  wsPurchase.columns = [
    { header: 'Field', key: 'field', width: 28 },
    { header: 'Value', key: 'value', width: 32 },
    { header: 'Example / Description', key: 'example', width: 50 },
  ];

  wsPurchase.addRow({ field: 'Supplier Name', value: '', example: 'e.g. Dubai Wholesale Laptops' });
  wsPurchase.addRow({ field: 'Supplier Code', value: '', example: 'e.g. SUP-001 (Optional)' });
  wsPurchase.addRow({ field: 'Supplier Invoice Number', value: '', example: 'e.g. INV-98214' });
  wsPurchase.addRow({ field: 'Purchase Date', value: new Date().toISOString().slice(0, 10), example: 'YYYY-MM-DD (e.g. 2026-09-16)' });
  wsPurchase.addRow({ field: 'Shipping Cost (PKR)', value: 0, example: 'e.g. 2500' });
  wsPurchase.addRow({ field: 'Other Purchase Cost (PKR)', value: 0, example: 'e.g. 1000 (Customs, clearing, etc.)' });
  wsPurchase.addRow({ field: 'Discount (PKR)', value: 0, example: 'e.g. 5000 (Negotiated supplier discount)' });
  wsPurchase.addRow({ field: 'Amount Paid (PKR)', value: 0, example: 'e.g. 100000 (Initial payment made, if any)' });
  wsPurchase.addRow({ field: 'Payment Method', value: 'cash', example: 'cash | bank_transfer | mobile_wallet | other' });
  wsPurchase.addRow({ field: 'Notes', value: '', example: 'Optional purchase order / batch notes' });

  // Style header row
  const purchaseHeader = wsPurchase.getRow(1);
  purchaseHeader.font = { bold: true, color: { argb: 'FFFFFFFF' } };
  purchaseHeader.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FF059669' }, // brand green
  };

  // 2. Laptops Sheet (HEADER ONLY - NO SAMPLE DATA ROWS)
  const wsLaptops = workbook.addWorksheet('Laptops', { views: [{ showGridLines: true }] });
  wsLaptops.columns = CANONICAL_LAPTOP_COLUMNS.map((col) => ({
    header: col,
    key: col.toLowerCase().replace(/[^a-z0-9]/g, '_'),
    width: Math.max(col.length + 5, 16),
  }));

  const laptopHeader = wsLaptops.getRow(1);
  laptopHeader.font = { bold: true, color: { argb: 'FFFFFFFF' } };
  laptopHeader.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FF0F172A' }, // slate 900
  };

  // 3. Accessories Sheet (HEADER ONLY)
  const wsAccessories = workbook.addWorksheet('Accessories', { views: [{ showGridLines: true }] });
  wsAccessories.columns = CANONICAL_ACCESSORY_COLUMNS.map((col) => ({
    header: col,
    key: col.toLowerCase().replace(/[^a-z0-9]/g, '_'),
    width: Math.max(col.length + 5, 16),
  }));

  const accHeader = wsAccessories.getRow(1);
  accHeader.font = { bold: true, color: { argb: 'FFFFFFFF' } };
  accHeader.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FF2563EB' }, // blue 600
  };

  // 4. Instructions Sheet
  const wsInstructions = workbook.addWorksheet('Instructions', { views: [{ showGridLines: true }] });
  wsInstructions.columns = [
    { header: 'Guideline / Rule', key: 'rule', width: 35 },
    { header: 'Specification & Explanation', key: 'details', width: 85 },
  ];

  const instrHeader = wsInstructions.getRow(1);
  instrHeader.font = { bold: true, color: { argb: 'FFFFFFFF' } };
  instrHeader.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FF475569' },
  };

  const guidelines = [
    { rule: '1. Clean Header-Only Template', details: 'This template contains no fake or sample stock rows. Fill in your genuine inventory rows directly.' },
    { rule: '2. One Laptop Per Row', details: 'Each serialized laptop unit must occupy its own row. Never combine multiple laptops into one line.' },
    { rule: '3. Identifiers', details: 'Serial Number, Service Tag, and Barcode are preserved as exact text. Never format them in scientific notation.' },
    { rule: '4. Condition Values', details: 'Allowed values: new, excellent, good, fair, poor. Defaults to "good" if blank.' },
    { rule: '5. Asking / Selling Prices', details: 'Missing prices must remain blank and will require owner review before listing. Prices are never manufactured.' },
    { rule: '6. Accessories Sheet', details: 'Record bulk parts (RAM, SSDs, Chargers, Mice, etc.) in the Accessories sheet. Existing accessories match by exact SKU.' },
    { rule: '7. AI & Supplier Spreadsheets', details: 'You do not have to use this template; custom supplier workbooks (.xlsx or .csv) can also be imported via AI mapping.' },
    { rule: '8. Atomic Confirmation', details: 'Spreadsheet extraction performs zero database writes. All records are previewed for review and confirmed via receive_purchase.' },
  ];

  guidelines.forEach((g) => wsInstructions.addRow(g));

  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
}

export async function downloadPurchaseImportTemplate(): Promise<void> {
  const blob = await generatePurchaseImportTemplate();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'Al-Kuwait-Tech-Purchase-Import-Template.xlsx';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
